// MACHINERY V2 (Stroje) — grouped 3-up card grid (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2. Reference impl:
// design_handoff_machinery_v3/machinery-v1-big.jsx (V1Body, GroupHeader).
//
// SECTIONS ARRIVE PRE-GROUPED from useFleetPageData, by the slice's
// `fleetGroupBy` (M2.3 ruling (i)) — the SAME map Fleet V2 renders as rows, so
// the two views can never section the same fleet differently. The per-group
// collapse mirrors the live grid's, and is keyed on the RAW group key so a
// language switch cannot silently re-expand a section.
//
// DESKTOP IS 3-UP (the sub-phase baseline). Tablet/phone tiers are M2.11, but
// this must not HARD-BREAK before then: the columns come from
// `shared/hooks/useBreakpoint` (never MUI useMediaQuery), stepping 3 -> 2 -> 1,
// and every card is `minWidth: 0` inside a `minmax(0, 1fr)` track so nothing
// overflows at an intermediate width.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import FleetCard from "./FleetCard";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import { MachineryV2Telemetry, unconnectedTelemetry } from "./cardStates";
import { FleetGroups } from "./fleetSelectors";
import { sectionTitle } from "./fleetGroupBy";

const GroupHeader: React.FC<{
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}> = ({ title, count, collapsed, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={!collapsed}
    style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "12px 14px", borderLeft: "2px solid var(--mod-accent)",
      border: 0, borderRadius: 8, background: "var(--mod-accent-soft)",
      cursor: "pointer", fontFamily: "inherit", textAlign: "left",
    }}
  >
    <span style={{ color: "var(--mod-accent)", fontSize: 11 }}>
      {collapsed ? "▸" : "▾"}
    </span>
    <span style={{ fontWeight: 600, color: "var(--ink)", flex: 1, minWidth: 0 }}>
      {title}
    </span>
    <span
      style={{
        fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mod-accent)",
        background: "var(--card)", padding: "2px 8px", borderRadius: 999,
        border: "1px solid var(--mod-accent-line)",
      }}
    >
      {count}
    </span>
  </button>
);

export interface FleetGridProps {
  groups: FleetGroups;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (name: string) => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetGrid: React.FC<FleetGridProps> = ({
  groups, telemetry, readiness, newMachineId, collapsedGroups, onToggleGroup,
  buildActions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const { isPhone, isTablet } = useBreakpoint();
  const cols = isPhone ? 1 : isTablet ? 2 : 3;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {Object.entries(groups).map(([key, list]) => {
        const collapsed = collapsedGroups.has(key);
        return (
          <div key={key} style={{ minWidth: 0 }}>
            <GroupHeader
              // Collapse keys on the RAW group key; only the label translates.
              title={sectionTitle(key, translate)}
              count={list.length}
              collapsed={collapsed}
              onToggle={() => onToggleGroup(key)}
            />
            {!collapsed && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gap: 16,
                  marginTop: 14,
                  minWidth: 0,
                }}
              >
                {list.map((m) => (
                  <FleetCard
                    key={m.id}
                    machine={m}
                    telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
                    readiness={readiness.get(m.id) || null}
                    isNew={m.id === newMachineId}
                    actions={buildActions(m)}
                    onOpenProfile={() => onOpenProfile(m)}
                    onOpenReadiness={() => onOpenReadiness(m)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FleetGrid;
